export type DeviceType = "mmb" | "smb" | "mdbn";

// Кириллица — только подписи для отображения. В API и БД значение — латиница.
export const DEVICE_TYPE_LABELS: Record<DeviceType, string> = {
  mmb: "ММБ",
  smb: "СМБ",
  mdbn: "МДБН",
};

export type DeviceFieldKey =
  | "serial"
  | "espVersion"
  | "stmVersion"
  | "limit"
  | "primary_current"
  | "ws_mmb_url"
  | "ws_mmb_port"
  | "meter_number"
  | "meter_address"
  | "last_reading"
  | "parent_module_id"
  | "timer"
  | "max_calc_load";


export interface DeviceTypeConfig {
  showFields: Partial<Record<DeviceFieldKey, boolean>>;
  editableFields: Partial<Record<DeviceFieldKey, boolean>>;
  tableMode: "stations" | "childModules";
  saveMode: "smb" | "standard" | "childModules";
}

export type FieldValidator = (value: string) => string | null;

const normalizeNumeric = (value: string) => value.trim().replace(",", ".");

export const required: FieldValidator = (value) =>
  value.trim() === "" ? "значение не может быть пустым" : null;

export const positiveNumber: FieldValidator = (value) => {
  const normalized = normalizeNumeric(value);
  if (!Number.isFinite(Number(normalized)) || normalized === "") {
    return "должно быть числом";
  }
  return Number(normalized) > 0 ? null : "должно быть числом больше 0";
};

export const nonNegativeNumber: FieldValidator = (value) => {
  const normalized = normalizeNumeric(value);
  if (!Number.isFinite(Number(normalized)) || normalized === "") {
    return "должно быть числом";
  }
  return Number(normalized) >= 0 ? null : "не может быть отрицательным";
};

export const positiveInteger: FieldValidator = (value) => {
  if (!/^\d+$/.test(value.trim())) {
    return "должно быть целым числом больше 0";
  }
  return Number(value.trim()) > 0 ? null : "должно быть целым числом больше 0";
};

export const portRange: FieldValidator = (value) => {
  if (!/^\d+$/.test(value.trim())) {
    return "порт должен быть целым числом";
  }
  const port = Number(value.trim());
  return port >= 1 && port <= 65535
    ? null
    : "порт должен быть в диапазоне 1-65535";
};

const URL_RE =
  /^(https?:\/\/)?(([\w-]+\.)+[a-z]{2,}|localhost|\d{1,3}(\.\d{1,3}){3})(:\d+)?([/?#]\S*)?$/i;

export const urlLike: FieldValidator = (value) =>
  URL_RE.test(value.trim()) ? null : "некорректный адрес";

export const FIELD_VALIDATORS: Partial<
  Record<DeviceFieldKey, FieldValidator[]>
> = {
  serial: [required],
  limit: [positiveNumber],
  primary_current: [positiveNumber],
  timer: [positiveInteger],
  max_calc_load: [nonNegativeNumber],
  ws_mmb_port: [portRange],
  ws_mmb_url: [required, urlLike],
};

export const DEVICE_TYPE_CONFIG: Record<DeviceType, DeviceTypeConfig> = {
  mmb: {
    showFields: {
      serial: true,
      espVersion: true,
      stmVersion: true,
      limit: false,
      primary_current: true,
      ws_mmb_url: true,
      ws_mmb_port: true,
      meter_number: true,
      meter_address: true,
      last_reading: true,
      parent_module_id: false,
      timer: true,
      max_calc_load: true
    },
    editableFields: {
      serial: true,
      limit: false,
      primary_current: true,
      ws_mmb_url: true,
      ws_mmb_port: true,
      meter_number: true,
      meter_address: true,
      timer: true,
      max_calc_load: true
    },
    tableMode: "childModules",
    saveMode: "childModules",
  },
  smb: {
    showFields: {
      serial: true,
      espVersion: true,
      stmVersion: true,
      limit: false,
      primary_current: true,
      ws_mmb_url: false,
      ws_mmb_port: false,
      meter_number: true,
      meter_address: true,
      last_reading: true,
      parent_module_id: true,
      timer: true,
    },
    editableFields: {
      serial: true,
      limit: false,
      primary_current: true,
      ws_mmb_url: false,
      ws_mmb_port: false,

    },
    tableMode: "stations",
    saveMode: "smb",
  },
  mdbn: {
    showFields: {
      serial: true,
      espVersion: true,
      stmVersion: true,
      limit: true,
      primary_current: true,
      ws_mmb_url: false,
      ws_mmb_port: false,
      meter_number: false,
      meter_address: false,
      last_reading: false,
      parent_module_id: false,
      timer: true,
    },
    editableFields: {
      serial: true,
      limit: true,
      primary_current: true,
      ws_mmb_url: false,
      ws_mmb_port: false,
      meter_number: false,
      meter_address: false,
      last_reading: false,
      timer: true,
    },
    tableMode: "stations",
    saveMode: "standard",
  },
};

/**
 * `G.2` (решение РП 22.10): модель шкафа МДБН — 9 или 15.
 *
 * У МДБН-9 физически девять станций из пятнадцати. Набор адресов задан **одной
 * константой**: если он понадобится в другом месте, его берут отсюда, а не
 * выписывают заново — иначе таблица и графики начнут показывать разное.
 */
export const MDBN9_CONTROLLER_ADDRESSES = [1, 2, 3, 6, 7, 8, 11, 12, 13];

export type CabinetModel = 9 | 15;

/** Подпись модели для карточки: `МДБН-9` или `МДБН-15`. */
export const CABINET_MODEL_LABELS: Record<CabinetModel, string> = {
  9: "МДБН-9",
  15: "МДБН-15",
};

/** Модель числом: всё, что не 9, — 15 (так у модулей до миграции). */
export function cabinetModelOf(value: unknown): CabinetModel {
  return Number(value) === 9 ? 9 : 15;
}

/**
 * Показывать ли станцию с таким адресом при такой модели шкафа.
 *
 * Скрытие — **только показ**: данные станции в базе не меняются, и станция вне
 * набора у МДБН-9 продолжает работать (это принято РП и названо в отчёте).
 */
export function isStationVisible(
  cabinetModel: unknown,
  controllerAddress: unknown,
): boolean {
  if (cabinetModelOf(cabinetModel) !== 9) return true;

  return MDBN9_CONTROLLER_ADDRESSES.includes(Number(controllerAddress));
}

export function getDeviceTypeConfig(
  deviceType?: string | null,
): DeviceTypeConfig {
  return (
    DEVICE_TYPE_CONFIG[(deviceType as DeviceType)] ?? DEVICE_TYPE_CONFIG.mdbn
  );
}
